import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import SupervisorDropdown from '../components/SupervisorDropdown';

interface Project {
  id: number;
  title: string;
  description?: string;
  status: string;
  student_name?: string;
  supervisor_name?: string;
  supervisor_id?: number | null;
  created_at: string;
  due_date?: string;
}

// Removed User interface since supervisor dropdown was removed for admin

const Projects: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | ''>('');
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyResults, setVerifyResults] = useState<Array<{ id: number; title: string; supervisor_id: number | null; db_name: string | null; reported_name: string | null }>>([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: projects = [] } = useQuery<Project[]>('projects', async () => {
    const response = await api.get('/projects');
    return response.data.projects;
  });

  // Removed users query since supervisor dropdown was removed for admin

  const createProjectMutation = useMutation(
    async (projectData: Partial<Project>) => {
      const response = await api.post('/projects', projectData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
        enqueueSnackbar('Project created successfully!', { variant: 'success' });
        setOpenDialog(false);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to create project', { variant: 'error' });
      },
    }
  );

  const updateProjectMutation = useMutation(
    async ({ id, data }: { id: number; data: Partial<Project> }) => {
      const response = await api.put(`/projects/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
        enqueueSnackbar('Project updated successfully!', { variant: 'success' });
        setOpenDialog(false);
        setEditingProject(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to update project', { variant: 'error' });
      },
    }
  );

  const deleteProjectMutation = useMutation(
    async (id: number) => {
      await api.delete(`/projects/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('projects');
        enqueueSnackbar('Project deleted successfully!', { variant: 'success' });
      },
      onError: (error: any) => {
        enqueueSnackbar(error.response?.data?.message || 'Failed to delete project', { variant: 'error' });
      },
    }
  );

  const handleSubmit = (formData: FormData) => {
    const data: Partial<Project> & { [key: string]: any } = {
      title: formData.get('title') as string,
      description: formData.get('description') as string,
      due_date: formData.get('due_date') as string,
      max_team_size: Number(formData.get('max_team_size') as string) || 1,
    };

    if (user?.role === 'supervisor') {
      data.supervisor_id = user.id;
    } else if (user?.role === 'admin' && selectedSupervisorId !== '') {
      data.supervisor_id = Number(selectedSupervisorId);
    }

    if (editingProject) {
      updateProjectMutation.mutate({ id: editingProject.id, data });
    } else {
      createProjectMutation.mutate(data);
    }
  };

  const handleOpenDialog = () => {
    setSelectedSupervisorId('');
    setEditingProject(null);
    setOpenDialog(true);
  };

  const handleEditProject = (project: Project) => {
    setSelectedSupervisorId(
      project.supervisor_id !== undefined && project.supervisor_id !== null
        ? Number(project.supervisor_id)
        : ''
    );
    setEditingProject(project);
    setOpenDialog(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'primary';
      case 'approved':
        return 'info';
      case 'submitted':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const verifySupervisors = async () => {
    try {
      const res = await api.get('/users/supervisors');
      const supervisors: Array<{ id: number; full_name: string }> = res.data.users || [];
      const map = new Map<number, string>(supervisors.map(s => [Number(s.id), String(s.full_name)]));
      const rows = projects.map(p => {
        const dbName = p.supervisor_id != null ? (map.get(Number(p.supervisor_id)) || null) : null;
        const reported = p.supervisor_name ?? null;
        return {
          id: p.id,
          title: p.title,
          supervisor_id: (p.supervisor_id as any) ?? null,
          db_name: dbName,
          reported_name: reported,
        };
      }).filter(r => {
        const a = (r.db_name || '').trim().toLowerCase();
        const b = (r.reported_name || '').trim().toLowerCase();
        return a !== b;
      });
      setVerifyResults(rows);
      setVerifyOpen(true);
    } catch (e) {
      setVerifyResults([{ id: -1, title: 'Error fetching supervisors', supervisor_id: null, db_name: null, reported_name: null }]);
      setVerifyOpen(true);
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesStatus = !filterStatus || project.status === filterStatus;
    const matchesSearch = !searchTerm || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.student_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // const students = users.filter(u => u.role === 'student'); // unused
  // const supervisors = users.filter(u => u.role === 'supervisor'); // unused

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Projects</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
        {user?.role === 'admin' && (
          <Button variant="outlined" onClick={verifySupervisors}>
            Verify Supervisors
          </Button>
        )}
        {(user?.role === 'admin' || user?.role === 'supervisor') && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenDialog}
          >
            New Project
          </Button>
        )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search projects"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Projects Grid */}
      <Grid container spacing={3}>
        {filteredProjects.map((project) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={project.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {project.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {project.description || 'No description'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    label={getStatusLabel(project.status)}
                    color={getStatusColor(project.status) as any}
                    size="small"
                  />
                </Box>
                {project.student_name && (
                  <Typography variant="body2" color="text.secondary">
                    Student: {project.student_name}
                  </Typography>
                )}
                {project.supervisor_name && (
                  <Typography variant="body2" color="text.secondary">
                    Supervisor: {project.supervisor_name}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  View
                </Button>
                {(user?.role === 'admin' || user?.role === 'supervisor') && (
                  <Button
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => handleEditProject(project)}
                  >
                    Edit
                  </Button>
                )}
                {user?.role === 'admin' && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => deleteProjectMutation.mutate(project.id)}
                  >
                    Delete
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Verify Supervisors Dialog */}
      <Dialog open={verifyOpen} onClose={() => setVerifyOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Supervisor Consistency Check</DialogTitle>
        <DialogContent>
          {verifyResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No mismatches detected.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {verifyResults.map(r => (
                <Paper key={r.id} sx={{ p: 2 }} variant="outlined">
                  <Typography variant="subtitle2">{r.title} (ID: {r.id})</Typography>
                  <Typography variant="body2" color="text.secondary">supervisor_id: {String(r.supervisor_id ?? 'null')}</Typography>
                  <Typography variant="body2">DB name by ID: {r.db_name || 'null'}</Typography>
                  <Typography variant="body2">Reported name from API: {r.reported_name || 'null'}</Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(new FormData(e.currentTarget)); }}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  name="title"
                  defaultValue={editingProject?.title}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  multiline
                  rows={3}
                  defaultValue={editingProject?.description}
                />
              </Grid>
              {user?.role === 'supervisor' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Supervisor"
                    value={user.full_name}
                    disabled
                    helperText="You will be assigned as the supervisor for this project"
                  />
                </Grid>
              )}
              {user?.role === 'admin' && (
                <Grid item xs={12} sm={6}>
                  <SupervisorDropdown
                    value={selectedSupervisorId}
                    onChange={setSelectedSupervisorId}
                    label="Supervisor"
                    required
                    showAvatar={true}
                    showRole={true}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  name="due_date"
                  type="date"
                  defaultValue={editingProject?.due_date?.split('T')[0]}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Team Size"
                  name="max_team_size"
                  type="number"
                  inputProps={{ min: 1 }}
                  defaultValue={1}
                  helperText="How many students should be assigned. Students will be auto-assigned randomly."
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingProject ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Projects; 